import UnauthenticatedLayout from '@/components/shared/layouts/unauthenticated-layout';
import { Button, Stack, Text, Title } from '@mantine/core';
import { IconHome } from '@tabler/icons-react';
import Link from 'next/link';

export default function TermsOfService() {
    return (
        <UnauthenticatedLayout title="Terms of Service">
            <Stack gap="4px" mb="lg">
                <Title>Terms of Service</Title>
                <Text size="sm">Last updated August 17, 2025</Text>
            </Stack>

            <Stack gap="md">
                {/* Header section */}
                <header>
                    <p>
                        <strong>Provider:</strong> DVMReach, Inc. (
                        <strong>“we”, “us”, “our”</strong>)
                    </p>
                    <p>
                        <strong>User:</strong> You or the entity you represent (
                        <strong>“you”, “your”</strong>)
                    </p>
                    <p>
                        By accessing or using our application (the <strong>“Service”</strong>), you
                        agree to be bound by these Terms of Service (these <strong>“Terms”</strong>
                        ). If you do not agree to these Terms, do not use the Service.
                    </p>
                </header>

                {/* 1. Acceptance & Eligibility */}
                <section>
                    <h2>1. Acceptance &amp; Eligibility</h2>
                    <ul>
                        <li>
                            You represent and warrant that you are legally authorized to enter into
                            these Terms on behalf of yourself or, if applicable, your organization.
                            If you are agreeing to these Terms on behalf of a company, organization,
                            or other legal entity, you confirm that you have the authority to bind
                            that entity to these Terms. You may not use the Service if you are
                            located in a country or jurisdiction subject to U.S. trade restrictions,
                            or if your use of the Service would violate any applicable laws, rules,
                            or regulations.
                        </li>
                        <li>
                            You must be at least 18 years old (or the age of majority in your
                            jurisdiction, if higher) to use the Service. By using the Service, you
                            represent that you meet this requirement.
                        </li>
                        <li>
                            You are responsible for ensuring that your use of the Service complies
                            with all applicable laws, regulations, and any agreements you may have
                            with third parties.
                        </li>
                    </ul>
                </section>

                {/* 2. Service Description & User Obligations */}
                <section>
                    <h2>2. Service Description &amp; User Obligations</h2>
                    <ul>
                        <li>
                            Our Services include, but are not limited to, outreach tools, VetEcho
                            voice recording, prescription management, appointment reminders,
                            SOAP-note generation, analytics, and integrations with practice
                            information management systems (PIMS) and application programming
                            interfaces (APIs). We may update, improve, or modify the Services from
                            time to time to enhance functionality, comply with applicable laws, or
                            address security concerns. When we make material changes, we will
                            provide you with reasonable notice.
                        </li>
                        <li>
                            We are committed to operating the Service in accordance with industry
                            best practices for security, confidentiality, and data integrity. For
                            more details on how we protect your information, please refer to our{' '}
                            <Link href="/privacy-policy">Privacy Policy</Link>.
                        </li>
                        <li>
                            You agree to:
                            <ul>
                                <li>
                                    Use the Service only for lawful purposes and in compliance with
                                    all applicable laws, regulations, and industry standards.
                                </li>
                                <li>
                                    Comply with all applicable third-party terms, such as software
                                    licenses, PIMS agreements, and API usage rules, when using
                                    integrations.
                                </li>
                                <li>
                                    Avoid using the Service in any manner that could damage,
                                    disable, overburden, or impair our systems or networks, or
                                    interfere with any other party’s use of the Service.
                                </li>
                                <li>
                                    Ensure that all information you provide to us is accurate,
                                    complete, and up to date.
                                </li>
                                <li>
                                    Promptly notify us if you become aware of any unauthorized
                                    access to your account or misuse of the Service.
                                </li>
                            </ul>
                        </li>
                    </ul>
                    <p>
                        In short: we’ll provide a secure, evolving platform designed for veterinary
                        practices, and you’ll use it responsibly and lawfully.
                    </p>
                </section>

                {/* 3. User Accounts & Security */}
                <section>
                    <h2>3. User Accounts &amp; Security</h2>
                    <ul>
                        <li>
                            To use certain features of the Service, you must create an account by
                            providing accurate, current, and complete information. You agree to keep
                            this information updated so that we can communicate with you about your
                            account and the Service.
                        </li>
                        <li>
                            You are responsible for maintaining the confidentiality and security of
                            your account credentials (including usernames, passwords, and any
                            multi-factor authentication codes) and for all activities that occur
                            under your account, whether authorized by you or not. You must notify us
                            immediately at <strong>security@dvmreach.com</strong> if you believe
                            your account or credentials have been compromised or used without
                            authorization.
                        </li>
                        <li>
                            You agree to:
                            <ul>
                                <li>Use strong, unique passwords and update them regularly.</li>
                                <li>Enable multi-factor authentication (MFA) if available.</li>
                                <li>
                                    Promptly notify us at <strong>security@dvmreach.com</strong> if
                                    you suspect or become aware of any unauthorized access to or use
                                    of your account.
                                </li>
                                <li>
                                    Take reasonable measure to prevent unauthorized access to
                                    devices you use to access the Service.
                                </li>
                            </ul>
                        </li>
                        <li>
                            If we detect or suspect unauthorized access to your account or data, we
                            may suspend access while we investigate. You agree to cooperate with our
                            security team during such investigations. In short, keep your account
                            information secure, tell us right away if something doesn’t look right,
                            and we’ll do the same.
                        </li>
                    </ul>
                </section>

                {/* 4. Fees, Payments & Subscription */}
                <section>
                    <h2>4. Fees, Payments &amp; Subscription</h2>
                    <ul>
                        <li>
                            Certain features of the Service require a paid subscription
                            (“Subscription”) and payment through one of our authorized payment
                            methods. Fees, billing cycles, and accepted payment methods will be
                            specified at the time you subscribe. Your Subscription will begin on the
                            date you complete your purchase unless otherwise stated. Unless
                            otherwise stated at the time of purchase, Subscriptions automatically
                            renew at the end of each billing period for the same term and at the
                            then-current rates. You may cancel your Subscription at any time through
                            your account settings or by contacting us at{' '}
                            <strong>billing@dvmreach.com</strong>. If you cancel, your Subscription
                            will remain active until the end of the current billing period, and you
                            will not be charged for the next cycle.
                        </li>
                        <li>
                            All fees are non-refundable except where required by law or as otherwise
                            expressly stated in these Terms. We encourage you to review your plan
                            carefully.
                        </li>
                        <li>
                            If we do not receive payment when due, we may suspend or terminate your
                            access to the Service until payment is made in full. We will provide you
                            with reasonable notice before suspension to allow you to resolve any
                            payment issues.
                        </li>
                    </ul>
                </section>

                {/* 5. Recording & Transcriptions; User Data & Responsibilities */}
                <section>
                    <h2>5. Recording &amp; Transcriptions; User Data &amp; Responsibilities</h2>

                    <h3>5.1 Recording &amp; Transcription</h3>
                    <ul>
                        <li>
                            You are solely responsible for complying with all applicable
                            communication and recording laws, including those requiring one-party or
                            all-party consent, as well as any applicable privacy regulations. This
                            responsibility includes, but is not limited to, providing required
                            notices and obtaining all necessary permissions before initiating a
                            recording using VetEcho or any similar tool within the Service.
                        </li>
                        <li>
                            At the start of each recording, you must:
                            <ul>
                                <li>Notify all participants that recording is taking place;</li>
                                <li>Clearly state the purpose of the recording; and</li>
                                <li>
                                    Give participants the option to decline to participate in the
                                    recording.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Important: We do not provide legal advice, and laws on recording vary by
                            jurisdiction. You should consult your own legal counsel to ensure
                            compliance.
                        </li>
                        <li>
                            You represent and warrant that:
                            <ul>
                                <li>
                                    Your use of the Service will comply with all applicable laws;
                                </li>
                                <li>
                                    You will provide all required notices and obtain all required
                                    consents; and
                                </li>
                                <li>
                                    You are solely responsible for reviewing and validating the
                                    accuracy of any transcription generated by VetEcho, as automated
                                    transcription may contain errors.
                                </li>
                            </ul>
                        </li>
                    </ul>

                    <h3>5.2 User Data License</h3>
                    <ul>
                        <li>
                            You retain all ownership rights in the materials and information you
                            provide through the Service (“User Data”), including, without
                            limitation, recordings, client/patient records, appointment data, and
                            any other information you submit or upload, provided that you have the
                            necessary rights to do so.
                        </li>
                        <li>
                            We may use, reproduce, modify, analyze, and create derivative works from
                            your data that has been anonymized and/or de-identified so that it
                            cannot reasonably be used to identify you, your clients, or your
                            patients, for purposes including but not limited to benchmarking,
                            analytics, market research, AI training, and improvement of our products
                            and services, all as described in our{' '}
                            <Link href="/privacy-policy">Privacy Policy</Link>.
                        </li>
                        <li>
                            Anonymization Commitment: When we use your data for analytics or
                            benchmarking, we will remove or alter personal identifiers so that the
                            information cannot reasonably be linked back to you, your clients, or
                            your patients.
                        </li>
                    </ul>

                    <h3>5.3 Responsibility for User-Provided Data</h3>
                    <ul>
                        <li>
                            You are solely responsible for:
                            <ul>
                                <li>
                                    The accuracy, quality, legality, and appropriateness of all User
                                    Data;
                                </li>
                                <li>
                                    Ensuring that your collection and use of User Data complies with
                                    all applicable laws, including privacy and data protection laws;
                                    and
                                </li>
                                <li>
                                    Any consequences arising from the provision or use of User Data.
                                </li>
                            </ul>
                        </li>
                        <li>
                            We are not responsible for any loss, damage, or liability arising from
                            your User Data or the actions of any person to whom you grant access to
                            your account.
                        </li>
                        <li>
                            If your account is terminated, you must request data retrieval before
                            termination by contacting <strong>support@dvmreach.com</strong>. After
                            termination, we may delete your data in accordance with our retention
                            policy.
                        </li>
                    </ul>

                    <h3>5.4 Storage &amp; Retention</h3>
                    <ul>
                        <li>
                            We store User Data in accordance with our data retention policy, which
                            is designed to comply with applicable laws and industry standards. We
                            will retain User Data only for as long as necessary to provide the
                            Service, comply with our legal obligations, resolve disputes, and
                            enforce our agreements.
                        </li>
                        <li>
                            Once data is deleted in accordance with our retention policy, it cannot
                            be recovered. We recommend you maintain your own backups of any critical
                            data.
                        </li>
                    </ul>

                    <h3>5.5 Use of PII</h3>
                    <ul>
                        <li>
                            Our handling of personally identifiable information (“PII”)—including
                            both your PII and the PII of your clients or patients—is governed by our{' '}
                            <Link href="/privacy-policy">Privacy Policy</Link>. By using the
                            Service, you consent to the processing of PII in the United States and
                            acknowledge that U.S. laws may differ from the laws of your
                            jurisdiction.
                        </li>
                        <li>
                            We will use client/patient PII only to provide the Service and for the
                            purposes stated in our{' '}
                            <Link href="/privacy-policy">Privacy Policy</Link>. We will not sell or
                            rent PII to third parties.
                        </li>
                        <li>
                            We only use client/patient PII included in User Data to provide the
                            Service and will not use it for other purposes beyond those in any
                            agreement with DVMReach and the{' '}
                            <Link href="/privacy-policy">Privacy Policy</Link>.
                        </li>
                        <li>
                            You are solely responsible for ensuring that your sharing of PII with
                            us, and your use of the Service in processing such PII, complies with
                            all applicable privacy and data protection laws, including obtaining all
                            necessary notices and consents from the individuals whose data you
                            process.
                        </li>
                        <li>
                            We implement reasonable and appropriate administrative, technical, and
                            physical safeguards designed to protect User Data against unauthorized
                            access, disclosure, alteration, and destruction. These measures include:
                            <ul>
                                <li>Encryption of data in transit and at rest;</li>
                                <li>
                                    Access controls to ensure that only authorized personnel with a
                                    legitimate business need can access User Data;
                                </li>
                                <li>
                                    Audit logging and monitoring to detect and respond to
                                    unauthorized activity; and
                                </li>
                                <li>
                                    Regular security testing and updates to address new threats.
                                </li>
                            </ul>
                        </li>
                        <li>
                            In the event of a security incident involving your User Data, we will:
                            <ul>
                                <li>Promptly investigate the incident;</li>
                                <li>Take necessary steps to mitigate potential harm; and</li>
                                <li>
                                    Notify you without undue delay, and in no event later than 3
                                    business days after confirming the incident, where required by
                                    applicable law.
                                </li>
                            </ul>
                        </li>
                        <li>
                            You acknowledge that no method of transmission or storage is completely
                            secure, and while we strive to use industry-standard means to protect
                            your data, we cannot guarantee absolute security.
                        </li>
                    </ul>
                </section>

                {/* 6. Acceptable Use & Restrictions */}
                <section>
                    <h2>6. Acceptable Use &amp; Restrictions</h2>

                    <h3>6.1 Acceptable Use</h3>
                    <ul>
                        <li>
                            You agree to use the Service only in ways that are lawful, ethical, and
                            consistent with these Terms. Your use must comply with all applicable
                            laws, regulations, and industry standards.
                        </li>
                    </ul>

                    <h3>6.2 Prohibited Activities</h3>
                    <ul>
                        <li>
                            You may not, directly or indirectly:
                            <ul>
                                <li>
                                    Use the Service in any unlawful manner or for any unlawful
                                    purpose;
                                </li>
                                <li>
                                    Access or attempt to access accounts, systems, or data without
                                    authorization;
                                </li>
                                <li>
                                    Interfere with or disrupt the integrity, security, or
                                    performance of the Service or its underlying systems;
                                </li>
                                <li>
                                    Introduce malicious code, malware, or other harmful content into
                                    the Service;
                                </li>
                                <li>
                                    Circumvent, disable, or otherwise interfere with security or
                                    authentication measures;
                                </li>
                                <li>
                                    Use the Service to infringe, misappropriate, or violate the
                                    rights of any third party, including intellectual property and
                                    privacy rights;
                                </li>
                                <li>
                                    Share, resell, or sublicense the Service without our prior
                                    written consent;
                                </li>
                                <li>
                                    Use automated scripts, bots, or scraping tools to access or
                                    collect data from the Service, or enable, authorize, assist, or
                                    encourage any third party to do so, except as expressly
                                    permitted by us; and
                                </li>
                                <li>
                                    Send spam or other unsolicited communications through or using
                                    the Service.
                                </li>
                            </ul>
                        </li>
                    </ul>

                    <h3>6.3 Protection of Security and Availability</h3>
                    <ul>
                        <li>
                            You must not engage in any activity that could compromise the
                            confidentiality, integrity, or availability of the Service, our systems,
                            or users’ data. We reserve the right to monitor usage patterns for the
                            purpose of maintaining security and service quality.
                        </li>
                    </ul>

                    <h3>6.4 Reporting Violations</h3>
                    <ul>
                        <li>
                            If you become aware of any violation of this Acceptable Use &amp;
                            Restrictions section, you must promptly report it to{' '}
                            <strong>security@dvmreach.com</strong>.
                        </li>
                    </ul>

                    <h3>6.5 Enforcement</h3>
                    <ul>
                        <li>
                            We reserve the right to investigate suspected violations of this
                            section. If we believe you have violated these restrictions, we may
                            suspend or terminate your access to the Service and/or remove any
                            related content, without prior notice where necessary to protect our
                            systems or comply with law.
                        </li>
                    </ul>
                </section>

                {/* 7. Indemnification */}
                <section>
                    <h2>7. Indemnification</h2>

                    <h3>7.1 Your Obligation to Indemnify</h3>
                    <ul>
                        <li>
                            You agree to defend, indemnify, and hold harmless DVMReach, Inc., its
                            affiliates, officers, directors, employees, agents, and licensors from
                            and against any claims, demands, liabilities, damages, losses, costs,
                            and expenses (including reasonable attorneys’ fees) arising out of or
                            related to:
                            <ul>
                                <li>Your use of the Service;</li>
                                <li>Your violation of these Terms or any applicable law;</li>
                                <li>
                                    Your infringement or misappropriation of any third-party rights,
                                    including intellectual property or privacy rights; or
                                </li>
                                <li>
                                    Any data, content, or materials you submit to or process through
                                    the Service.
                                </li>
                            </ul>
                        </li>
                    </ul>
                </section>

                {/* 8. Dispute Resolution & Arbitration */}
                <section>
                    <h2>8. Dispute Resolution &amp; Arbitration</h2>

                    <h3>8.1 Informal Resolution</h3>
                    <ul>
                        <li>
                            <strong>
                                Before either of us initiates formal legal action, we agree to try
                                in good faith to resolve any dispute, claim, or controversy arising
                                out of or relating to these Terms or the Service (“Dispute”) through
                                informal discussions. If we cannot resolve the Dispute within 30
                                days after notice is given, either of us may proceed as described
                                below.
                            </strong>
                        </li>
                    </ul>

                    <h3>8.2 Agreement to Arbitrate</h3>
                    <ul>
                        <li>
                            If unresolved, disputes (except small claims or injunctive relief
                            matters) will be resolved through binding arbitration administered by
                            the American Arbitration Association under its Commercial Arbitration
                            Rules.
                        </li>
                        <li>
                            A single arbitrator in Salt Lake City, Utah will conduct the process.
                        </li>
                        <li>
                            The arbitrator’s decision will be final and may be entered into court.
                        </li>
                        <li>
                            You and DVMReach each waive the right to a trial and to participate in
                            class actions or class arbitration for any disputes relating to or
                            arising out of the Service or these Terms.
                        </li>
                    </ul>
                </section>

                {/* 9. Disclaimer of Warranties & Limitation of Liability */}
                <section>
                    <h2>9. Disclaimer of Warranties &amp; Limitation of Liability</h2>

                    <h3>9.1 Disclaimer of Warranties</h3>
                    <ul>
                        <li>
                            <strong>
                                The Service, including all features, content, and materials, is
                                provided “as is” and “as available,” without warranties of any kind,
                                whether express, implied, statutory, or otherwise.
                            </strong>
                        </li>
                        <li>
                            <strong>
                                To the fullest extent permitted by law, we disclaim all warranties,
                                including any implied warranties of merchantability, fitness for a
                                particular purpose, title, and non-infringement, and any warranties
                                arising out of course of dealing or usage of trade.
                            </strong>
                        </li>
                        <li>
                            <strong>
                                We do not warrant that the Service will be uninterrupted, secure,
                                error-free, or free from viruses or other harmful components, or
                                that any defects will be corrected.
                            </strong>
                        </li>
                        <li>
                            <strong>
                                We are not responsible for the accuracy, completeness, or
                                reliability of any data or content provided through the Service.
                            </strong>
                        </li>
                    </ul>

                    <h3>9.2 Limitation of Liability</h3>
                    <ul>
                        <li>
                            To the maximum extent permitted by law, DVMReach, Inc., its affiliates,
                            and their respective officers, directors, employees, agents, and
                            licensors will not be liable for any:
                            <ul>
                                <li>
                                    Indirect, incidental, consequential, special, punitive, or
                                    exemplary damages;
                                </li>
                                <li>Loss of profits, revenue, goodwill, or data;</li>
                                <li>Business interruption; or</li>
                                <li>Cost of substitute goods or services,</li>
                            </ul>
                            arising out of or in connection with the use of, or inability to use,
                            the Service, whether based on warranty, contract, tort (including
                            negligence), or any other legal theory, even if we have been advised of
                            the possibility of such damages.
                        </li>
                    </ul>

                    <h3>9.3 Jurisdiction-Specific Exceptions</h3>
                    <ul>
                        <li>
                            Some jurisdictions do not allow certain warranty disclaimers or
                            limitations of liability, so the above limitations may not apply to you
                            in full. In such cases, our liability will be limited to the maximum
                            extent permitted by applicable law.
                        </li>
                    </ul>
                </section>

                {/* 10. Modifications */}
                <section>
                    <h2>10. Modifications</h2>

                    <h3>10.1 Right to Modify</h3>
                    <ul>
                        <li>
                            We may modify these Terms from time to time to reflect changes in our
                            Services, legal requirements, or business practices.
                        </li>
                    </ul>

                    <h3>10.2 Notice of Changes</h3>
                    <ul>
                        <li>
                            If we make material changes, we will provide you with reasonable notice
                            before the changes take effect. We may give notice by posting the
                            updated Terms on our website, sending an email to the address associated
                            with your account, or by other reasonable means.
                        </li>
                    </ul>

                    <h3>10.3 Effective Date of Changes</h3>
                    <ul>
                        <li>
                            Unless otherwise stated in our notice, changes will take effect on the
                            date specified in the notice. Your continued use of the Service after
                            the effective date of any updated Terms constitutes your acceptance of
                            those changes. If you do not agree to the updated Terms, you must stop
                            using the Service before the effective date of the changes. In such
                            cases, you may request termination of your account and, if applicable, a
                            prorated refund of any prepaid fees for unused services, unless
                            otherwise stated in your agreement with us.
                        </li>
                    </ul>
                    <p>
                        We may update these Terms at any time. Material changes will be
                        communicated. Continuing to use the Service after updates signifies your
                        acceptance.
                    </p>
                </section>

                {/* 11. Termination */}
                <section>
                    <h2>11. Termination</h2>

                    <h3>11.1 Termination by You</h3>
                    <ul>
                        <li>
                            You may terminate your account and stop using the Service at any time by
                            following the instructions in your account settings or by contacting us
                            at <strong>support@dvmreach.com</strong>. Notwithstanding the foregoing,
                            we may retain and use anonymized and/or de-identified data in accordance
                            with Section 5.2 and our Privacy Policy. If you have a paid
                            Subscription, termination will take effect at the end of your current
                            billing cycle unless otherwise stated.
                        </li>
                    </ul>

                    <h3>11.2 Termination by Us</h3>
                    <ul>
                        <li>
                            We may suspend or terminate your access to the Service, in whole or in
                            part, if:
                            <ul>
                                <li>You violate these Terms or any applicable law;</li>
                                <li>You fail to pay fees when due;</li>
                                <li>
                                    We reasonably believe your use of the Service poses a security
                                    or legal risk to us, our users, or third parties; or
                                </li>
                                <li>We discontinue the Service.</li>
                            </ul>
                            Unless we determine immediate action is necessary to comply with law or
                            protect our systems, we will provide you with at least{' '}
                            <strong>10 days’ notice</strong> and an opportunity to resolve the issue
                            before terminating your account.
                        </li>
                    </ul>
                </section>

                {/* 12. Governing Law */}
                <section>
                    <h2>12. Governing Law</h2>
                    <p>
                        These Terms are governed by the laws of the State of Utah, excluding
                        conflict-of-law provisions.
                    </p>
                </section>

                {/* 13. Contact Information */}
                <section>
                    <h2>13. Contact Information</h2>
                    <p>
                        For questions, data access, export, or any concerns, contact us at{' '}
                        <strong>info@dvmreach.com</strong>.
                    </p>
                </section>
            </Stack>

            <Button
                mt="lg"
                mx="auto"
                display="block"
                maw="250px"
                color="black"
                leftSection={<IconHome size="1rem" />}
                component={Link}
                href="/"
            >
                dvmGateway Home
            </Button>
        </UnauthenticatedLayout>
    );
}
